import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  FaCalendarAlt,
  FaCheck,
  FaEdit,
  FaLayerGroup,
  FaLock,
  FaPlus,
  FaTrash,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import {
  createCommissionInstallments,
  deleteCommissionInstallment,
  updateCommissionInstallment,
} from "../commissionsApi";
import { formatCurrency, formatPercent } from "./commissionDetailConstants";
import {
  resolveInstallmentCommissionAmount,
  resolvePlanTuitionInstallmentBudget,
} from "./commissionPlanUtils";
import {
  INSTALLMENT_FREQUENCY_OPTIONS,
  buildInstallmentBatchCreatePayload,
  generateCommissionInstallments,
  resolveInstallmentGroupSplit,
  toInputDate,
} from "./commissionInstallmentUtils";
import {
  analyzeBudgetAllocation,
  splitInstallmentsByLock,
  sumInstallmentPaidTuition,
} from "./tuitionFeeSyncUtils";
import {
  resolveInstallmentInvoicedTuition,
  resolveInstallmentRemainingTuition,
} from "./commissionInstallmentUtils";

const controlClass =
  "h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-[12px] font-medium text-slate-800 outline-none transition focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20 disabled:bg-slate-50";

const amountControlClass = `${controlClass} tabular-nums`;

function sanitizeMoneyInput(value) {
  if (value === "" || value === null || value === undefined) return "";
  let cleaned = String(value).replace(/[^\d.]/g, "");
  const dotIndex = cleaned.indexOf(".");
  if (dotIndex !== -1) {
    cleaned =
      cleaned.slice(0, dotIndex + 1) +
      cleaned.slice(dotIndex + 1).replace(/\./g, "");
  }
  return cleaned;
}

function roundMoney(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

function Chip({ children, tone = "slate" }) {
  const tones = {
    slate: "border-slate-200 bg-slate-50 text-slate-600",
    blue: "border-[#007BFF]/20 bg-[#007BFF]/8 text-[#007BFF]",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    green: "border-emerald-200 bg-emerald-50 text-emerald-700",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${tones[tone] || tones.slate}`}
    >
      {children}
    </span>
  );
}

function isPendingInstallment(row) {
  if (String(row?.status || "").toLowerCase() === "paid") return false;
  const label = String(
    row?.commissionStatusLabel || row?.status || "pending",
  ).toLowerCase();
  return label === "pending";
}

/** Pending rows with no invoice linkage or invoiced tuition may be deleted. */
function isInstallmentDeletable(row) {
  if (!row?.id || !isPendingInstallment(row)) return false;
  if (row?.financeInvoiceId || row?.invoiceId) return false;
  const allocs = row.financeAllocations ?? row.finance_allocations ?? [];
  if (Array.isArray(allocs) && allocs.length > 0) return false;
  if (resolveInstallmentInvoicedTuition(row) > 0.009) return false;
  return true;
}

function canEditInstallmentRow(row) {
  const status = String(row?.status || "").toLowerCase();
  return status !== "cancelled" && status !== "void";
}

function isInstallmentDueDateLocked(row) {
  return Boolean(row?.financeInvoiceId || row?.invoiceId);
}

function resolveRemainingCommission(row) {
  const balance = row?.balanceCommission ?? row?.balance_commission;
  if (balance != null && balance !== "" && !Number.isNaN(Number(balance))) {
    return Math.max(0, Number(balance));
  }
  const commission =
    Number(row?.commissionAmount ?? row?.commission_amount) || 0;
  const received =
    Number(row?.commissionReceived ?? row?.commission_received) || 0;
  return Math.max(0, Math.round((commission - received) * 100) / 100);
}

function CreateInstallmentModal({
  open,
  onClose,
  plan,
  currency,
  onSaved,
  mode = "create",
  tuitionBudget = null,
  nextInstallmentNo = 1,
}) {
  const isAppend = mode === "append";
  const effectiveBudget = Math.max(
    0,
    Number(
      tuitionBudget != null
        ? tuitionBudget
        : (plan?.commissionableTuitionFee ?? plan?.tuitionFee),
    ) || 0,
  );
  const [frequency, setFrequency] = useState(isAppend ? "custom" : "monthly");
  const [count, setCount] = useState(isAppend ? "1" : "4");
  const [startDate, setStartDate] = useState(
    toInputDate(new Date().toISOString()),
  );
  const [preview, setPreview] = useState([]);
  const [groupAmount, setGroupAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const lastSplitBaseRef = useRef(null);

  const { splitBase, userSpecifiedAmount } = useMemo(
    () => resolveInstallmentGroupSplit(groupAmount, effectiveBudget),
    [groupAmount, effectiveBudget],
  );

  useEffect(() => {
    if (!open) return;
    setFrequency(isAppend ? "custom" : "monthly");
    setCount(isAppend ? "1" : "4");
    setStartDate(toInputDate(new Date().toISOString()));
    setGroupAmount("");
    setPreview([]);
    lastSplitBaseRef.current = null;
    setSaving(false);
    setFormError("");
  }, [open, isAppend]);

  useEffect(() => {
    if (!open || !plan?.id) {
      setPreview([]);
      return;
    }
    if (effectiveBudget <= 0) {
      setPreview([]);
      lastSplitBaseRef.current = null;
      return;
    }
    const planTuition =
      Number(plan.commissionableTuitionFee ?? plan.tuitionFee) || effectiveBudget;
    const preserveRowAmounts = lastSplitBaseRef.current === splitBase;
    setPreview((prev) => {
      const rows = generateCommissionInstallments({
        tuitionFee: splitBase,
        planTuitionFee: planTuition,
        commissionRate: plan.commissionRate,
        commissionGstType: plan.commissionGstType,
        commissionType: plan.commissionType || "percentage",
        count: Number(count) || 1,
        frequency,
        startDate,
      });

      return rows.map((row, index) => {
        const previous = prev[index];
        const tuitionAmount =
          preserveRowAmounts &&
          previous?.tuition_amount != null &&
          previous?.tuition_amount !== ""
            ? previous.tuition_amount
            : row.tuition_amount;
        const commissionAmount = resolveInstallmentCommissionAmount({
          tuitionFee: Number(tuitionAmount) || 0,
          planTuitionFee: planTuition,
          commissionType: plan.commissionType || "percentage",
          commissionRate: plan.commissionRate,
          commissionGstType: plan.commissionGstType || "exclusive",
        });
        return {
          ...row,
          installment_no: Number(nextInstallmentNo) + index,
          due_date:
            frequency === "custom"
              ? index === 0
                ? toInputDate(startDate)
                : previous?.due_date || ""
              : row.due_date,
          tuition_amount: tuitionAmount,
          commission_amount: commissionAmount,
          invoice_amount: commissionAmount,
          remarks: previous?.remarks ?? row.remarks,
        };
      });
    });
    lastSplitBaseRef.current = splitBase;
  }, [
    open,
    plan,
    count,
    frequency,
    startDate,
    splitBase,
    effectiveBudget,
    nextInstallmentNo,
  ]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape" && !saving) onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, saving]);

  const updatePreviewDueDate = (index, value) => {
    setPreview((prev) =>
      prev.map((row, i) => (i === index ? { ...row, due_date: value } : row)),
    );
  };

  const updatePreviewField = (index, field, value) => {
    setPreview((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );
  };

  const updatePreviewMoney = (index, field, rawValue) => {
    if (field !== "tuition_amount") return;
    setFormError("");
    const cleaned = sanitizeMoneyInput(rawValue);
    setPreview((prev) => {
      const othersSum = prev.reduce(
        (sum, row, i) =>
          i === index ? sum : sum + (Number(row.tuition_amount) || 0),
        0,
      );
      const batchCap = splitBase > 0 ? splitBase : effectiveBudget;
      const maxForRow =
        Math.round(Math.max(0, batchCap - othersSum) * 100) / 100;
      let amount = cleaned === "" ? "" : Number(cleaned);
      if (amount !== "" && Number.isFinite(amount) && amount > maxForRow) {
        amount = maxForRow;
      }

      return prev.map((row, i) => {
        if (i !== index) return row;
        const tuition =
          amount === "" ? "" : Math.round((Number(amount) || 0) * 100) / 100;
        const planTuition =
          Number(plan?.commissionableTuitionFee ?? plan?.tuitionFee) ||
          effectiveBudget;
        const commissionAmount = resolveInstallmentCommissionAmount({
          tuitionFee: Number(tuition) || 0,
          planTuitionFee: planTuition,
          commissionType: plan?.commissionType || "percentage",
          commissionRate: plan?.commissionRate,
          commissionGstType: plan?.commissionGstType || "exclusive",
        });
        return {
          ...row,
          tuition_amount: tuition,
          commission_amount: commissionAmount,
          invoice_amount: commissionAmount,
        };
      });
    });
  };

  const canCreatePreview =
    effectiveBudget > 0 &&
    preview.length > 0 &&
    preview.every((row) => (Number(row.tuition_amount) || 0) > 0) &&
    (frequency !== "custom" ||
      preview.every((row) => Boolean(toInputDate(row.due_date))));

  const totals = useMemo(
    () =>
      preview.reduce(
        (acc, row) => {
          acc.tuition += Number(row.tuition_amount) || 0;
          acc.commission += Number(row.commission_amount) || 0;
          return acc;
        },
        { tuition: 0, commission: 0 },
      ),
    [preview],
  );

  const handleCreate = async () => {
    if (!plan?.id || !canCreatePreview) return;
    setFormError("");
    if (
      preview.some((row) => !(Number(row.tuition_amount) > 0))
    ) {
      setFormError("Enter a tuition amount for each installment.");
      return;
    }
    if (frequency === "custom" && !canCreatePreview) {
      setFormError("Please set a due date for every custom installment.");
      return;
    }
    const groupCap = userSpecifiedAmount ?? effectiveBudget;
    if (totals.tuition > groupCap + 0.009) {
      setFormError(
        `Tuition total ${formatCurrency(totals.tuition, currency)} exceeds this group amount ${formatCurrency(groupCap, currency)}.`,
      );
      return;
    }
    if (totals.tuition > effectiveBudget + 0.009) {
      setFormError(
        `Tuition total ${formatCurrency(totals.tuition, currency)} exceeds available ${formatCurrency(effectiveBudget, currency)}.`,
      );
      return;
    }
    if (effectiveBudget <= 0) {
      setFormError(
        "There is no remaining tuition fee to create installments for.",
      );
      return;
    }
    setSaving(true);
    try {
      await createCommissionInstallments(
        plan.id,
        buildInstallmentBatchCreatePayload({
          frequency,
          count,
          previewLength: preview.length,
          startDate,
          userSpecifiedAmount,
          installments: preview.map((row) => ({
            installment_no: row.installment_no,
            due_date: row.due_date,
            tuition_amount: Number(row.tuition_amount) || 0,
            commission_rate: row.commission_rate,
            commission_amount: Number(row.commission_amount) || 0,
            commission_gst_type: row.commission_gst_type,
            invoice_amount: Number(row.commission_amount) || 0,
            status: row.status || "pending",
            remarks: row.remarks || null,
          })),
        }),
      );
      Swal.fire({
        icon: "success",
        title: isAppend ? "Installments added" : "Installments created",
        text: isAppend
          ? "Additional installments saved for remaining tuition."
          : "Tuition fee installment plan saved.",
        timer: 1400,
        showConfirmButton: false,
      });
      onClose();
      if (typeof onSaved === "function") onSaved();
    } catch (error) {
      setFormError(error?.message || "Failed to create installments.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <>
      <div
        className="cms-app-modal-backdrop bg-black/40"
        onClick={saving ? undefined : onClose}
        aria-hidden="true"
      />
      <div
        className="cms-app-modal cms-app-modal--centered flex items-center justify-center p-3 sm:p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="commission-installment-modal-title"
        onClick={saving ? undefined : onClose}
      >
        <div
          className="tailwind-container relative z-10 flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl"
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <div className="flex items-center gap-2.5 border-b border-slate-100 bg-gradient-to-r from-[#F7FAFF] to-white px-4 py-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#007BFF] text-white">
              <FaLayerGroup size={13} />
            </div>
            <div className="min-w-0 flex-1">
              <h3
                id="commission-installment-modal-title"
                className="mb-0 text-[15px] font-semibold text-slate-900"
              >
                {isAppend
                  ? "Add tuition fee installment"
                  : "Create tuition fee installment"}
              </h3>
              <p className="mb-0 text-[11px] text-slate-400">
                {isAppend
                  ? "Add installments using remaining tuition only"
                  : "Enter an amount for this group, set frequency and count, save — repeat with another amount and frequency for the rest"}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 disabled:opacity-50"
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <label className="col-span-2 block min-w-0 sm:col-span-4">
                <span className="mb-1 block text-[10px] font-semibold uppercase text-slate-400">
                  Amount for these installments (optional)
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  className={amountControlClass}
                  placeholder={`Max ${formatCurrency(effectiveBudget, currency)}`}
                  value={groupAmount}
                  disabled={saving || effectiveBudget <= 0}
                  onChange={(event) => {
                    setFormError("");
                    const cleaned = sanitizeMoneyInput(event.target.value);
                    if (cleaned === "") {
                      setGroupAmount("");
                      return;
                    }
                    const parsed = Number(cleaned);
                    if (!Number.isFinite(parsed)) return;
                    const capped = Math.min(
                      roundMoney(parsed),
                      effectiveBudget,
                    );
                    setGroupAmount(String(capped));
                  }}
                />
                <p className="mb-0 mt-1 text-[10px] text-slate-500">
                  Leave blank to use full available{" "}
                  <strong>{formatCurrency(effectiveBudget, currency)}</strong>
                  {userSpecifiedAmount != null &&
                  userSpecifiedAmount < effectiveBudget - 0.009
                    ? " · After save, add another group for the rest"
                    : null}
                </p>
              </label>
              <label className="block min-w-0">
                <span className="mb-1 block text-[10px] font-semibold uppercase text-slate-400">
                  Frequency
                </span>
                <select
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value)}
                  className={controlClass}
                  disabled={saving}
                >
                  {INSTALLMENT_FREQUENCY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block min-w-0">
                <span className="mb-1 block text-[10px] font-semibold uppercase text-slate-400">
                  Count
                </span>
                <input
                  type="number"
                  min="1"
                  max="60"
                  value={count}
                  onChange={(e) => setCount(e.target.value)}
                  className={controlClass}
                  disabled={saving}
                />
              </label>
              <label className="col-span-2 block min-w-0">
                <span className="mb-1 block text-[10px] font-semibold uppercase text-slate-400">
                  {frequency === "custom" ? "First due date" : "Start date"}
                </span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className={controlClass}
                  disabled={saving}
                />
              </label>
            </div>

            <div className="rounded-lg border border-[#007BFF]/20 bg-[#007BFF]/5 px-3 py-2 text-[11px] text-[#007BFF]">
              Example: enter 1,000 with monthly, save, then add 5,000 with
              quarterly. Frequency sets due dates; count splits your group
              amount across rows. Edit row amounts if needed. Commission
              follows the plan rate.
            </div>

            <div className="flex flex-wrap gap-3 text-[11px] text-slate-500">
              <span>
                This group{" "}
                <strong className="text-slate-700">
                  {formatCurrency(totals.tuition, currency)}
                </strong>
                {splitBase > 0 ? (
                  <>
                    {" / "}
                    {formatCurrency(splitBase, currency)}
                  </>
                ) : null}
              </span>
              <span>
                Left on plan after save{" "}
                <strong className="text-emerald-700">
                  {formatCurrency(
                    Math.max(0, effectiveBudget - totals.tuition),
                    currency,
                  )}
                </strong>
              </span>
              <span>
                Expected Commission total{" "}
                <strong className="text-[#007BFF]">
                  {formatCurrency(plan?.expectedCommission, currency)}
                </strong>
              </span>
              <span>
                Commission total{" "}
                <strong className="text-[#007BFF]">
                  {formatCurrency(totals.commission, currency)}
                </strong>
              </span>
              <span>
                {plan?.commissionType === "fixed"
                  ? `Amount ${formatCurrency(plan?.commissionRate, currency)}`
                  : `Rate ${formatPercent(plan?.commissionRate, 2)}`}
              </span>
            </div>

            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="min-w-full border-collapse text-left text-[12px]">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-400">
                  <tr>
                    <th className="px-2.5 py-2 font-semibold">#</th>
                    <th className="px-2.5 py-2 font-semibold">Due date</th>
                    <th className="px-2.5 py-2 font-semibold">Tuition</th>
                    <th className="px-2.5 py-2 font-semibold">Commission</th>
                    <th className="px-2.5 py-2 font-semibold">Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map((row, rowIndex) => (
                    <tr
                      key={`p-${row.installment_no}`}
                      className="border-t border-slate-100"
                    >
                      <td className="px-2.5 py-2 font-semibold text-slate-700">
                        {row.installment_no}
                      </td>
                      <td className="px-2.5 py-2">
                        {frequency === "custom" ? (
                          <input
                            type="date"
                            className={controlClass}
                            value={row.due_date || ""}
                            onChange={(e) =>
                              updatePreviewDueDate(rowIndex, e.target.value)
                            }
                          />
                        ) : (
                          <span className="inline-flex items-center gap-1 text-slate-700">
                            <FaCalendarAlt
                              size={9}
                              className="text-slate-400"
                            />
                            {row.due_date}
                          </span>
                        )}
                      </td>
                      <td className="px-2.5 py-2 tabular-nums">
                        <input
                          type="text"
                          inputMode="decimal"
                          className={amountControlClass}
                          placeholder="0.00"
                          value={
                            row.tuition_amount === "" ||
                            row.tuition_amount == null
                              ? ""
                              : String(row.tuition_amount)
                          }
                          disabled={saving}
                          onChange={(e) =>
                            updatePreviewMoney(
                              rowIndex,
                              "tuition_amount",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td className="px-2.5 py-2 tabular-nums font-semibold text-[#007BFF]">
                        {formatCurrency(row.commission_amount, currency)}
                      </td>
                      <td className="min-w-[8rem] px-2.5 py-2">
                        <input
                          type="text"
                          className={controlClass}
                          value={row.remarks || ""}
                          placeholder="Remarks"
                          disabled={saving}
                          onChange={(e) =>
                            updatePreviewField(
                              rowIndex,
                              "remarks",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {formError ? (
            <div className="border-t border-rose-100 bg-rose-50 px-4 py-2.5">
              <p className="mb-0 text-[12px] font-medium text-rose-700">
                {formError}
              </p>
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50/80 px-4 py-3">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="inline-flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 text-[12px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCreate}
              disabled={
                saving || !canCreatePreview || effectiveBudget <= 0
              }
              className={`inline-flex h-9 items-center gap-1.5 rounded-lg px-3.5 text-[12px] font-semibold text-white ${
                saving || !canCreatePreview || effectiveBudget <= 0
                  ? "cursor-not-allowed bg-[#007BFF]/40"
                  : "bg-[#007BFF] hover:bg-[#0069D9]"
              }`}
              style={{ border: "none" }}
            >
              <FaCheck size={11} />
              {saving
                ? "Saving…"
                : isAppend
                  ? "Add tuition fee installments"
                  : "Save tuition fee installment"}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

function EditTuitionInstallmentModal({
  open,
  row,
  plan,
  currency,
  installmentTuitionBudget = null,
  otherTuitionTotal = 0,
  invoicedTuitionFloor = 0,
  dueDateLocked = false,
  onClose,
  onSaved,
}) {
  const planTuition = Math.max(
    0,
    Number(
      installmentTuitionBudget != null
        ? installmentTuitionBudget
        : plan?.commissionableTuitionFee ?? plan?.tuitionFee,
    ) || 0,
  );
  const maxTuition = Math.max(
    0,
    Math.round((planTuition - Number(otherTuitionTotal || 0)) * 100) / 100,
  );
  const rate = Number(plan?.commissionRate) || 0;
  const gstType = plan?.commissionGstType || "none";

  const minTuition = Math.max(0, roundMoney(Number(invoicedTuitionFloor) || 0));
  const [dueDate, setDueDate] = useState("");
  const [initialTuition, setInitialTuition] = useState(0);
  const [tuition, setTuition] = useState("");
  const [commission, setCommission] = useState(0);
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const dueDateInputRef = useRef(null);

  const openDueDatePicker = () => {
    if (saving || dueDateLocked) return;
    const input = dueDateInputRef.current;
    if (!input || typeof input.showPicker !== "function") return;
    try {
      input.showPicker();
    } catch {
      // Browser may block if not a direct user gesture.
    }
  };

  const syncCommissionPreview = useCallback(
    (amount) => {
      const planTuition = Number(
        plan?.commissionableTuitionFee ?? plan?.tuitionFee,
      );
      setCommission(
        resolveInstallmentCommissionAmount({
          tuitionFee: amount,
          planTuitionFee: planTuition,
          commissionType: plan?.commissionType || "percentage",
          commissionRate: rate,
          commissionGstType: gstType,
        }) || 0,
      );
    },
    [plan?.commissionType, plan?.commissionableTuitionFee, plan?.tuitionFee, rate, gstType],
  );

  useEffect(() => {
    if (!open || !row) return;
    const tuitionValue = roundMoney(
      Number(row.tuitionAmount ?? row.tuition_amount) || 0,
    );
    setDueDate(toInputDate(row.dueDate) || "");
    setInitialTuition(tuitionValue);
    setTuition(tuitionValue > 0 ? String(tuitionValue) : "");
    syncCommissionPreview(tuitionValue);
    setRemarks(row.remarks || "");
    setSaving(false);
    setFormError("");
  }, [open, row, syncCommissionPreview]);

  if (!open || !row) return null;

  const tuitionAmount = roundMoney(Number(tuition) || 0);
  const planBudgetFreed = Math.max(
    0,
    roundMoney(initialTuition - tuitionAmount),
  );
  const planBudgetLeft = Math.max(0, roundMoney(maxTuition - tuitionAmount));
  const existingPaidTuition = roundMoney(
    Number(row.studentPaidTuition ?? row.student_paid_tuition) || 0,
  );
  const tuitionGuide =
    minTuition > 0.009
      ? `Enter ${formatCurrency(minTuition, currency)} to ${formatCurrency(maxTuition, currency)}`
      : `Enter up to ${formatCurrency(maxTuition, currency)}`;

  const updateTuition = (rawValue) => {
    const next = sanitizeMoneyInput(rawValue);
    setTuition(next);
    setFormError("");
    if (next !== "" && next !== "." && Number.isFinite(Number(next))) {
      syncCommissionPreview(roundMoney(Number(next)));
    }
  };

  const commitTuition = () => {
    if (tuition === "" || tuition === ".") {
      const fallback = minTuition > 0 ? minTuition : initialTuition;
      setTuition(String(fallback));
      syncCommissionPreview(fallback);
      return;
    }
    let amount = roundMoney(Number(tuition) || 0);
    if (amount < minTuition) amount = minTuition;
    if (amount > maxTuition) amount = maxTuition;
    setTuition(String(amount));
    syncCommissionPreview(amount);
  };

  const handleSave = async () => {
    setFormError("");

    if (!dueDateLocked && !dueDate) {
      setFormError("Due date is required.");
      return;
    }

    let finalTuition;
    if (tuition === "" || tuition === ".") {
      finalTuition = minTuition > 0 ? minTuition : initialTuition;
    } else {
      finalTuition = roundMoney(Number(tuition) || 0);
      if (finalTuition < minTuition) finalTuition = minTuition;
      if (finalTuition > maxTuition) finalTuition = maxTuition;
    }
    setTuition(String(finalTuition));
    syncCommissionPreview(finalTuition);

    const finalCommission = resolveInstallmentCommissionAmount({
      tuitionFee: finalTuition,
      planTuitionFee: Number(
        plan?.commissionableTuitionFee ?? plan?.tuitionFee,
      ),
      commissionType: plan?.commissionType || "percentage",
      commissionRate: rate,
      commissionGstType: gstType,
    });

    const budgetFreed = Math.max(0, roundMoney(initialTuition - finalTuition));

    if (finalTuition < 0) {
      setFormError("Tuition amount cannot be negative.");
      return;
    }
    if (finalTuition + 0.009 < minTuition) {
      setFormError(
        `Tuition cannot be below ${formatCurrency(minTuition, currency)} already on invoice for this installment.`,
      );
      return;
    }
    if (finalTuition > maxTuition + 0.009) {
      setFormError(
        `Tuition ${formatCurrency(finalTuition, currency)} exceeds available ${formatCurrency(maxTuition, currency)} for this installment.`,
      );
      return;
    }

    setSaving(true);
    try {
      await updateCommissionInstallment(row.id, {
        due_date: dueDate,
        tuition_amount: finalTuition,
        student_paid_tuition: existingPaidTuition,
        commission_rate: rate,
        commission_amount: Number(finalCommission) || 0,
        commission_gst_type: gstType,
        invoice_amount: Number(finalCommission) || 0,
        remarks: remarks || null,
      });
      Swal.fire({
        icon: "success",
        title: "Installment updated",
        text:
          budgetFreed > 0.009
            ? `${formatCurrency(budgetFreed, currency)} freed for new installments.`
            : undefined,
        timer: 1400,
        showConfirmButton: false,
      });
      onClose?.();
      onSaved?.();
    } catch (error) {
      setFormError(error?.message || "Failed to update installment.");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <>
      <div
        className="cms-app-modal-backdrop bg-black/40"
        onClick={saving ? undefined : onClose}
        aria-hidden="true"
      />
      <div
        className="cms-app-modal cms-app-modal--centered flex items-center justify-center p-3 sm:p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-tuition-installment-modal-title"
        onClick={saving ? undefined : onClose}
      >
        <div
          className="tailwind-container relative z-10 flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl"
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <div className="flex items-center gap-2.5 border-b border-slate-100 bg-gradient-to-r from-[#F7FAFF] to-white px-4 py-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#007BFF] text-white">
              <FaEdit size={13} />
            </div>
            <div className="min-w-0 flex-1">
              <h3
                id="edit-tuition-installment-modal-title"
                className="mb-0 text-[15px] font-semibold text-slate-900"
              >
                Edit tuition fee installment #{row.installmentNo}
              </h3>
              <p className="mb-0 text-[11px] text-slate-400">
                Lower tuition to match what is already on invoice(s) to free
                the rest for new installments.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 disabled:opacity-50"
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
            {minTuition > 0.009 ? (
              <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-[11px] text-sky-800">
                {formatCurrency(minTuition, currency)} is already on commission
                invoice(s). Lower this installment tuition to{" "}
                {formatCurrency(minTuition, currency)} to free{" "}
                {formatCurrency(
                  Math.max(0, roundMoney(tuitionAmount - minTuition)),
                  currency,
                )}{" "}
                for new installments.
              </div>
            ) : null}

            <div className="flex flex-wrap gap-3 text-[11px] text-slate-500">
              <span>
                Tuition{" "}
                <strong className="text-slate-700">
                  {formatCurrency(tuitionAmount, currency)}
                </strong>
                {" / "}
                {formatCurrency(maxTuition, currency)} max
              </span>
              {minTuition > 0.009 ? (
                <span>
                  On invoice (min){" "}
                  <strong className="text-[#007BFF]">
                    {formatCurrency(minTuition, currency)}
                  </strong>
                </span>
              ) : null}
              <span>
                Plan budget left{" "}
                <strong className="text-emerald-700">
                  {formatCurrency(planBudgetLeft, currency)}
                </strong>
              </span>
              <span>
                Commission{" "}
                <strong className="text-[#007BFF]">
                  {formatCurrency(commission, currency)}
                </strong>
              </span>
              <span>Rate {formatPercent(rate, 2)}</span>
            </div>

            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600">
              {tuitionGuide}. Commission updates automatically from the plan
              rate.
            </div>

            {planBudgetFreed > 0.009 ? (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] text-emerald-800">
                Saving will free{" "}
                {formatCurrency(planBudgetFreed, currency)} for additional
                installments.
              </div>
            ) : null}

            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="min-w-full border-collapse text-left text-[12px]">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-400">
                  <tr>
                    <th className="px-2.5 py-2 font-semibold">#</th>
                    <th className="px-2.5 py-2 font-semibold">Due date</th>
                    <th className="px-2.5 py-2 font-semibold">Tuition fee</th>
                    <th className="px-2.5 py-2 font-semibold">Commission</th>
                    <th className="px-2.5 py-2 font-semibold">Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-slate-100">
                    <td className="px-2.5 py-2 font-semibold text-slate-700">
                      {row.installmentNo}
                    </td>
                    <td className="px-2.5 py-2">
                      <input
                        ref={dueDateInputRef}
                        type="date"
                        className={controlClass}
                        value={dueDate}
                        disabled={saving || dueDateLocked}
                        onClick={openDueDatePicker}
                        onChange={(e) => {
                          setDueDate(e.target.value);
                          setFormError("");
                        }}
                      />
                    </td>
                    <td className="px-2.5 py-2 tabular-nums">
                      <input
                        type="text"
                        inputMode="decimal"
                        className={amountControlClass}
                        value={tuition}
                        disabled={saving}
                        placeholder={tuitionGuide}
                        title={tuitionGuide}
                        onChange={(e) => updateTuition(e.target.value)}
                        onBlur={commitTuition}
                      />
                    </td>
                    <td className="px-2.5 py-2 tabular-nums font-semibold text-[#007BFF]">
                      {formatCurrency(commission, currency)}
                    </td>
                    <td className="min-w-[8rem] px-2.5 py-2">
                      <input
                        type="text"
                        className={controlClass}
                        value={remarks}
                        placeholder="Remarks"
                        disabled={saving}
                        onChange={(e) => setRemarks(e.target.value)}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {formError ? (
            <div className="border-t border-rose-100 bg-rose-50 px-4 py-2.5">
              <p className="mb-0 text-[12px] font-medium text-rose-700">
                {formError}
              </p>
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50/80 px-4 py-3">
            <button
              type="button"
              disabled={saving}
              onClick={onClose}
              className="inline-flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 text-[12px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={handleSave}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-[#007BFF] px-3.5 text-[12px] font-semibold text-white hover:bg-[#0069D9] disabled:opacity-60"
              style={{ border: "none" }}
            >
              <FaCheck size={11} />
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

function CommissionInstallmentsCard({
  plan,
  bonus = null,
  installments = [],
  currency = "AUD",
  clientId = null,
  returnTo = "",
  onSaved,
}) {
  const hasPlan = Boolean(plan?.id);
  const savedRows = useMemo(
    () => (Array.isArray(installments) ? installments : []),
    [installments],
  );
  const hasSaved = savedRows.length > 0;

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [editRow, setEditRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedInstallmentIds, setSelectedInstallmentIds] = useState([]);

  const totals = useMemo(
    () =>
      savedRows.reduce(
        (acc, row) => {
          acc.tuition += Number(row.tuitionAmount) || 0;
          acc.commission += Number(row.commissionAmount) || 0;
          return acc;
        },
        { tuition: 0, commission: 0 },
      ),
    [savedRows],
  );

  const planTuition = resolvePlanTuitionInstallmentBudget(plan, bonus);
  const totalPaidTuition = sumInstallmentPaidTuition(savedRows);
  const remainingTuition = Math.max(
    0,
    Math.round((planTuition - totals.tuition) * 100) / 100,
  );
  const remainingCommissionableAfterPaid = Math.max(
    0,
    Math.round((planTuition - totalPaidTuition) * 100) / 100,
  );
  const allocation = analyzeBudgetAllocation(totals.tuition, planTuition);
  const { locked: lockedRows } = splitInstallmentsByLock(savedRows);
  const canAddInstallment = hasSaved && remainingTuition > 0.009;
  const nextInstallmentNo =
    savedRows.reduce(
      (max, row) => Math.max(max, Number(row.installmentNo) || 0),
      0,
    ) + 1;

  const openCreateModal = () => {
    setModalMode("create");
    setModalOpen(true);
  };

  const openAddModal = () => {
    if (!canAddInstallment) {
      Swal.fire(
        "No remaining tuition",
        "All tuition fee is already allocated to installments.",
        "info",
      );
      return;
    }
    setModalMode("append");
    setModalOpen(true);
  };

  const deletableInstallmentIds = useMemo(
    () =>
      savedRows
        .filter((row) => isInstallmentDeletable(row))
        .map((row) => String(row.id)),
    [savedRows],
  );

  useEffect(() => {
    setSelectedInstallmentIds((prev) =>
      prev.filter((id) => deletableInstallmentIds.includes(String(id))),
    );
  }, [deletableInstallmentIds]);

  const toggleInstallmentSelection = (row) => {
    if (!isInstallmentDeletable(row)) return;
    const id = String(row.id);
    setSelectedInstallmentIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const toggleSelectAllDeletableInstallments = () => {
    if (selectedInstallmentIds.length === deletableInstallmentIds.length) {
      setSelectedInstallmentIds([]);
      return;
    }
    setSelectedInstallmentIds([...deletableInstallmentIds]);
  };

  const handleDeleteInstallment = async (row) => {
    const installmentId = row?.id;
    if (!installmentId || !isInstallmentDeletable(row)) return;
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Delete installment?",
      text: `Remove installment #${row.installmentNo || ""}? This cannot be undone.`,
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
    });
    if (!confirm.isConfirmed) return;

    setSaving(true);
    try {
      // POST /api/v1/new-commission-installments/{id}/delete
      await deleteCommissionInstallment(installmentId);
      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Installment removed.",
        timer: 1400,
        showConfirmButton: false,
      });
      if (typeof onSaved === "function") onSaved();
    } catch (error) {
      Swal.fire(
        "Error",
        error?.message || "Failed to delete installment.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSelectedInstallments = async () => {
    const rows = savedRows.filter(
      (row) =>
        selectedInstallmentIds.includes(String(row.id)) &&
        isInstallmentDeletable(row),
    );
    if (!rows.length) return;

    const confirm = await Swal.fire({
      icon: "warning",
      title: "Delete selected installments?",
      text: `Remove ${rows.length} installment(s)? This cannot be undone.`,
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
    });
    if (!confirm.isConfirmed) return;

    setSaving(true);
    try {
      for (const row of rows) {
        await deleteCommissionInstallment(row.id);
      }
      setSelectedInstallmentIds([]);
      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: `${rows.length} installment(s) removed.`,
        timer: 1400,
        showConfirmButton: false,
      });
      if (typeof onSaved === "function") onSaved();
    } catch (error) {
      Swal.fire(
        "Error",
        error?.message || "Failed to delete installment(s).",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const allDeletableSelected =
    deletableInstallmentIds.length > 0 &&
    selectedInstallmentIds.length === deletableInstallmentIds.length;

  return (
    <>
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-2 border-b border-slate-100 bg-gradient-to-r from-[#F7FAFF] to-white px-3 py-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[#007BFF] text-white">
            <FaLayerGroup size={11} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <h3 className="mb-0 text-[13px] font-semibold text-slate-900">
                Installments
              </h3>
              {hasSaved ? (
                <Chip tone="green">{savedRows.length} saved</Chip>
              ) : hasPlan ? (
                <Chip tone="amber">No plan yet</Chip>
              ) : (
                <Chip tone="amber">Needs plan</Chip>
              )}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {selectedInstallmentIds.length > 0 ? (
              <button
                type="button"
                onClick={handleDeleteSelectedInstallments}
                disabled={saving}
                className="inline-flex h-7 items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 text-[10px] font-semibold text-red-700 hover:bg-red-100 disabled:opacity-60"
                style={{ border: "none" }}
              >
                <FaTrash size={9} />
                Delete selected ({selectedInstallmentIds.length})
              </button>
            ) : null}
            {canAddInstallment ? (
              <button
                type="button"
                onClick={openAddModal}
                disabled={saving}
                className="inline-flex h-7 items-center gap-1 rounded-md bg-[#007BFF] px-2 text-[10px] font-semibold text-white hover:bg-[#0069D9] disabled:opacity-60"
                style={{ border: "none" }}
              >
                <FaPlus size={9} />
                Add installment
              </button>
            ) : null}
          </div>
        </div>

        <div className="p-2.5">
          {!hasPlan ? (
            <div className="flex flex-col items-center justify-center py-5 text-center">
              <FaLock className="mb-1.5 text-slate-300" size={14} />
              <p className="mb-0 text-[12px] font-semibold text-slate-700">
                Create commission plan first
              </p>
            </div>
          ) : !hasSaved ? (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-dashed border-slate-200 bg-slate-50/60 px-3 py-3">
              <p className="mb-0 text-[12px] text-slate-500">
                No tuition fee installment yet
              </p>
              <button
                type="button"
                onClick={openCreateModal}
                className="inline-flex h-8 items-center gap-1.5 rounded-md bg-[#007BFF] px-2.5 text-[11px] font-semibold text-white hover:bg-[#0069D9]"
                style={{ border: "none" }}
              >
                <FaPlus size={10} />
                Create plan
              </button>
            </div>
              ) : (
            <div className="space-y-2">
              {totalPaidTuition > 0.009 ? (
                <div className="flex flex-wrap gap-3 rounded-lg border border-emerald-200 bg-emerald-50/80 px-3 py-2 text-[11px] text-emerald-900">
                  <span>
                    Paid tuition recorded{" "}
                    <strong>{formatCurrency(totalPaidTuition, currency)}</strong>
                  </span>
                  <span>
                    Remaining commissionable{" "}
                    <strong>
                      {formatCurrency(remainingCommissionableAfterPaid, currency)}
                    </strong>
                  </span>
                  {remainingTuition > 0.009 ? (
                    <span>
                      Unallocated for new installments{" "}
                      <strong>{formatCurrency(remainingTuition, currency)}</strong>
                    </span>
                  ) : null}
                </div>
              ) : null}
              {allocation.isOver ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
                  <p className="mb-1 text-[12px] font-semibold text-amber-900">
                    Installments exceed tuition by{" "}
                    {formatCurrency(allocation.overBy, currency)}
                  </p>
                  <p className="mb-0 text-[11px] text-amber-800/90">
                    Edit pending installments to lower tuition/commission
                    amounts, or delete individual pending rows
                    {lockedRows.length > 0
                      ? `. ${lockedRows.length} invoiced installment(s) cannot be deleted — edit and lower tuition to free remaining budget.`
                      : "."}
                  </p>
                </div>
              ) : null}
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="min-w-full border-collapse text-left text-[12px]">
                  <thead className="bg-slate-50 text-[11px] text-slate-500">
                    <tr>
                      <th className="w-8 px-2 py-2">
                        {deletableInstallmentIds.length > 0 ? (
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 rounded border-slate-300"
                            checked={allDeletableSelected}
                            disabled={saving}
                            aria-label="Select all deletable installments"
                            onChange={toggleSelectAllDeletableInstallments}
                          />
                        ) : null}
                      </th>
                      <th className="px-2.5 py-2 font-semibold">#</th>
                      <th className="px-2.5 py-2 font-semibold">Due date</th>
                      <th className="px-2.5 py-2 text-end font-semibold">
                        Tuition fee
                      </th>
                      <th className="px-2.5 py-2 text-end font-semibold">
                        Paid tuition fee
                      </th>
                      <th className="px-2.5 py-2 text-end font-semibold">
                        On invoice
                      </th>
                      <th className="px-2.5 py-2 text-end font-semibold">
                        Remaining tuition
                      </th>
                      <th className="px-2.5 py-2 text-end font-semibold">
                        Commission
                      </th>
                      <th className="px-2.5 py-2 text-end font-semibold">
                        Received commission
                      </th>
                      <th className="px-2.5 py-2 text-end font-semibold">
                        Remaining commission
                      </th>
                      <th className="px-2.5 py-2 font-semibold">Status</th>
                      <th className="px-2.5 py-2 font-semibold">Invoice</th>
                      <th className="px-2.5 py-2 text-right font-semibold">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {savedRows.map((row) => {
                      const invoiceId = row.invoiceId || row.financeInvoiceId;
                      const invoicePath =
                        clientId && invoiceId
                          ? `/commissions/edit-invoice/${clientId}/${invoiceId}`
                          : null;
                      const canEdit = canEditInstallmentRow(row);
                      const canDelete = isInstallmentDeletable(row);
                      const invoicedTuition =
                        resolveInstallmentInvoicedTuition(row);
                      const isSelected = selectedInstallmentIds.includes(
                        String(row.id),
                      );
                      const remainingInstallmentTuition =
                        resolveInstallmentRemainingTuition(row);
                      return (
                        <tr key={row.id} className="border-t border-slate-100">
                          <td className="px-2 py-2 align-middle">
                            {canDelete ? (
                              <input
                                type="checkbox"
                                className="h-3.5 w-3.5 rounded border-slate-300"
                                checked={isSelected}
                                disabled={saving}
                                aria-label={`Select installment ${row.installmentNo}`}
                                onChange={() => toggleInstallmentSelection(row)}
                              />
                            ) : (
                              <span
                                className="inline-block h-3.5 w-3.5"
                                title="Linked to an invoice — cannot delete"
                              />
                            )}
                          </td>
                          <td className="px-2.5 py-2 font-semibold text-slate-700">
                            {row.installmentNo}
                          </td>
                          <td className="px-2.5 py-2">
                            <span className="inline-flex items-center gap-1 text-slate-700">
                              <FaCalendarAlt
                                size={9}
                                className="text-slate-400"
                              />
                              {row.dueDate}
                            </span>
                          </td>
                          <td className="px-2.5 py-2 text-end tabular-nums text-slate-800">
                            {formatCurrency(row.tuitionAmount, currency)}
                          </td>
                          <td className="px-2.5 py-2 text-end tabular-nums text-emerald-700">
                            {formatCurrency(
                              row.studentPaidTuition ??
                                row.student_paid_tuition,
                              currency,
                            )}
                          </td>
                          <td
                            className="px-2.5 py-2 text-end tabular-nums text-[#007BFF]"
                            title={
                              invoicedTuition > 0.009
                                ? "Total tuition on invoice line(s) from this installment"
                                : undefined
                            }
                          >
                            {formatCurrency(invoicedTuition, currency)}
                          </td>
                          <td
                            className="px-2.5 py-2 text-end tabular-nums text-amber-700"
                            title="Tuition not yet on invoice — edit down or add new installments manually"
                          >
                            {formatCurrency(
                              remainingInstallmentTuition,
                              currency,
                            )}
                          </td>
                          <td className="px-2.5 py-2 text-end tabular-nums font-medium text-slate-800">
                            {formatCurrency(row.commissionAmount, currency)}
                          </td>
                          <td className="px-2.5 py-2 text-end tabular-nums font-medium text-[#007BFF]">
                            {formatCurrency(
                              row.commissionReceived ?? row.commission_received,
                              currency,
                            )}
                          </td>
                          <td className="px-2.5 py-2 text-end tabular-nums font-medium text-amber-700">
                            {formatCurrency(
                              resolveRemainingCommission(row),
                              currency,
                            )}
                          </td>
                          <td className="px-2.5 py-2">
                            <Chip
                              tone={
                                row.status === "paid"
                                  ? "green"
                                  : row.financeInvoiceId
                                    ? "blue"
                                    : "amber"
                              }
                            >
                              {row.status === "paid"
                                ? "Paid"
                                : row.commissionStatusLabel ||
                                  row.status ||
                                  "pending"}
                            </Chip>
                          </td>
                          <td className="px-2.5 py-2">
                            {invoiceId ? (
                              invoicePath ? (
                                <Link
                                  to={invoicePath}
                                  state={{ returnTo }}
                                  className="text-[11px] font-semibold text-[#007BFF] hover:underline"
                                  style={{ color: "#007BFF" }}
                                >
                                  {row.invoiceNumber || `#${invoiceId}`}
                                </Link>
                              ) : (
                                <span className="text-[11px] font-semibold text-slate-600">
                                  {row.invoiceNumber || `#${invoiceId}`}
                                </span>
                              )
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-2.5 py-2 text-right">
                            {canEdit || canDelete ? (
                              <div className="inline-flex items-center justify-end gap-1">
                                {canEdit ? (
                                  <button
                                    type="button"
                                    title={
                                      invoicedTuition > 0.009
                                        ? "Edit installment — lower tuition to free remaining amount"
                                        : "Edit installment"
                                    }
                                    aria-label={
                                      invoicedTuition > 0.009
                                        ? "Edit installment — lower tuition to free remaining amount"
                                        : "Edit installment"
                                    }
                                    onClick={() => setEditRow(row)}
                                    disabled={saving}
                                    className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 disabled:opacity-60"
                                  >
                                    <FaEdit size={10} />
                                  </button>
                                ) : null}
                                {canDelete ? (
                                  <button
                                    type="button"
                                    title="Delete installment"
                                    aria-label="Delete installment"
                                    onClick={() => handleDeleteInstallment(row)}
                                    disabled={saving}
                                    className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-60"
                                  >
                                    <FaTrash size={10} />
                                  </button>
                                ) : null}
                              </div>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      <CreateInstallmentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        plan={plan}
        currency={currency}
        onSaved={onSaved}
        mode={modalMode}
        tuitionBudget={modalMode === "append" ? remainingTuition : planTuition}
        nextInstallmentNo={modalMode === "append" ? nextInstallmentNo : 1}
      />
      <EditTuitionInstallmentModal
        open={Boolean(editRow)}
        row={editRow}
        plan={plan}
        currency={currency}
        installmentTuitionBudget={planTuition}
        invoicedTuitionFloor={
          editRow ? resolveInstallmentInvoicedTuition(editRow) : 0
        }
        dueDateLocked={editRow ? isInstallmentDueDateLocked(editRow) : false}
        otherTuitionTotal={Math.max(
          0,
          totals.tuition - (Number(editRow?.tuitionAmount) || 0),
        )}
        onClose={() => setEditRow(null)}
        onSaved={onSaved}
      />
    </>
  );
}

export default CommissionInstallmentsCard;
